import React, { useEffect, useState } from "react";
import styled from "styled-components/native";
import { Controller, useForm } from "react-hook-form";
import { schema } from "@scenes/authentication/utils/DeleteAccountSchema";
import { yupResolver } from "@hookform/resolvers/yup";
import { useDeleteAccount, useDeleteAccountDisclaimer } from "@apis/user";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import useLogout from "@components/common/hooks/useLogout";
import { useTranslation } from "react-i18next";
import Loader from "@components/atoms/Loader";
import MessageContainer from "@components/molecules/MessageContainer";
import Popup from "@components/molecules/PopUp";
import { Button, Container, Input, Spacer } from "@components/atoms";
import { ButtonCategory, ButtonType } from "@components/atoms/Button/types";
import { SpaceType } from "@components/atoms/Spacer/types";
import { messageType } from "@components/molecules/PopUp/types";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { useTheme } from "styled-components/native";
import LayoutContainer from "@components/layout/LayoutContainer";
import { useNavigation } from "@react-navigation/native";
import { useIsFoldable } from "@navigations/utils/FoldableWrapper";
import WebView from "react-native-webview";
import { bodyStyleToCSS } from "@utils/product/productFormatters";
import { Platform } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";

const DeleteAccount = () => {
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const { deviceWidth: width } = useIsFoldable();
  const typography = useTypographyStore();
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [apiError, setApiError] = useState<string>("");

  const userInfo = useUserInfo();
  const DeleteAccountAPI = useDeleteAccount(userInfo.getUserId());
  const handleLogout = useLogout();
  //ToDo: Need to handle logic for showing popup on login screen after deleting user.
  const { i18n, t } = useTranslation();

  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const navigation = useNavigation();
  const deleteAccountDisclaimer = useDeleteAccountDisclaimer();
  const [htmlContent, setHtmlContent] = useState<{ html: string }>({
    html: "",
  });

  useEffect(() => {
    if (!theme.isMobile) {
      navigation.setOptions({
        headerLeft: () => <></>,
      });
    }
    const fetchHtmlContent = async () => {
      const data =
        deleteAccountDisclaimer.data?.data?.account_delete_disclaimer
          ?.translations[i18n.language]?.body;
      if (data) {
        setHtmlContent({ html: data });
      }
    };
    fetchHtmlContent();
  }, [deleteAccountDisclaimer.isSuccess, i18n.language, theme.isMobile]);

  const onSubmit = (data: any) => {
    DeleteAccountAPI.mutate(
      {
        password: data.password,
      },
      {
        onSuccess: (response: any) => {
          const responseData = response.data;
          const deletedStatus = responseData.data.deleted;
          if (deletedStatus) {
            setShowDeletePopup(false);
            handleLogout();
          } else {
            const errorMessage = responseData.message;
            setApiError(errorMessage);
          }
        },
      }
    );
  };

  const closePopup = () => {
    setShowDeletePopup(false);
    setApiError("");
    reset();
  };
  const html = `<html>
      <head>
        <meta name="viewport" content="width=device-width, initial-scale=1">
        <style>
          body {
            ${bodyStyleToCSS(typography["Body-R"])}
            color: ${theme["core-05"]};
          }
        </style>
      </head>
      <body>
        <div>
          ${htmlContent.html}
        </div>
      </body>
    </html>
`;

  return (
    <>
      <LayoutContainer>
        <ParentContainer>
          {deleteAccountDisclaimer.isLoading &&
            !deleteAccountDisclaimer.isError && <Loader />}
          {deleteAccountDisclaimer.isSuccess && htmlContent.html && (
            <>
              <Spacer size={SpaceType.ExtraSmall} />
              <WebViewContainer>
                <WebView
                  source={{ html: html }}
                  style={[
                    {
                      backgroundColor: "transparent",
                    },
                    theme.isMobile ? { width: width } : {},
                  ]}
                  showsVerticalScrollIndicator={false}
                />
              </WebViewContainer>
              {(theme.isLargeScreen || theme.isTablet) && (
                <TabletButtonContainer>
                  <Button
                    type={ButtonType.primary}
                    category={ButtonCategory.default}
                    title={t("deleteAccount.button.delete.text")}
                    onPress={() => setShowDeletePopup(true)}
                  />
                </TabletButtonContainer>
              )}
              <BottomContainer insets={insets}>
                <Container>
                  {theme.isMobile && (
                    <Button
                      type={ButtonType.primary}
                      category={ButtonCategory.default}
                      title={t("deleteAccount.button.delete.text")}
                      onPress={() => setShowDeletePopup(true)}
                    />
                  )}
                  {showDeletePopup && (
                    <Popup
                      title={t("deleteAccount.popUp.title.text")}
                      buttons={[
                        {
                          type: ButtonType.primary,
                          title: t("deleteAccount.popUp.button.delete.text"),
                          onPressAction: handleSubmit(onSubmit),
                          loading: DeleteAccountAPI.isLoading,
                          disabled: DeleteAccountAPI.isLoading || !isValid,
                        },
                        {
                          type: ButtonType.secondary,
                          title: t("deleteAccount.popUp.button.cancel.text"),
                          onPressAction: () => closePopup(),
                        },
                      ]}
                      HtmlSource={{
                        html: t("deleteAccount.popUp.discription.text"),
                      }}
                      styleKey="Body-R"
                      type={messageType.Error}
                      hasChildren={true}
                    >
                      <Controller
                        control={control}
                        render={({
                          field: { onChange, onBlur, value },
                          formState: { errors },
                        }) => (
                          <Input
                            inputLabel={t(
                              "deleteAccount.popUp.input.inputLabel.text"
                            )}
                            placeholder={t(
                              "deleteAccount.popUp.input.placeholder.text"
                            )}
                            hideIconContainer={true}
                            secureTextEntry={true}
                            onBlur={onBlur}
                            onChangeText={(Value) => {
                              onChange(Value);
                              if (apiError) {
                                setApiError("");
                              }
                            }}
                            value={value}
                            error={errors?.password?.message || apiError}
                            disabled={DeleteAccountAPI.isLoading}
                          />
                        )}
                        name="password"
                      />
                    </Popup>
                  )}
                </Container>
              </BottomContainer>
            </>
          )}
          {deleteAccountDisclaimer.isError && (
            <MessageContainer
              buttons={{
                type: ButtonType.primary,
                title: t("deleteAccount.message.button.retry.text"),
                onPressAction: () => deleteAccountDisclaimer.refetch(),
                loading: deleteAccountDisclaimer.isLoading,
                disabled: deleteAccountDisclaimer.isLoading,
              }}
              internetPreference={true}
            />
          )}
        </ParentContainer>
      </LayoutContainer>
    </>
  );
};

const ParentContainer = styled.View`
  flex-grow: 1;
  justify-content: space-between;
`;

const TabletButtonContainer = styled.View`
  flex-grow: 1;
  padding-horizontal: 40px;
`;

const BottomContainer = styled.View<{ insets: { bottom: number } }>`
  margin-bottom: ${({ insets }) =>
    Platform.OS === "android"
      ? insets.bottom > 0
        ? insets.bottom + 5
        : 40
      : 40}px;
`;

const WebViewContainer = styled.View`
  flex: 1;
  min-height: 125px;
`;

export default DeleteAccount;
